import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApi } from '@/contexts/AuthContext';
import { MapPin, Loader2, Waves, Flame, Wind, Activity, CloudHail, CloudLightning, Anchor, ShieldAlert, Tornado } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const zColor = z => /not_assessed/i.test(z || '') ? C.slate : /high|extreme|yes|elevated/i.test(z || '') ? C.red : /med|moderate/i.test(z || '') ? C.amber : C.green;
const BASIS_BADGE = {
  measured: { label: 'Measured', bg: 'rgba(29,158,117,0.12)', color: C.green },
  not_assessed: { label: 'Not assessed', bg: 'rgba(92,107,122,0.12)', color: C.slate },
};
const HASH_BADGE = { label: 'Hash', bg: 'rgba(226,75,74,0.1)', color: C.red };
const DETERMINED = [['flood_zone', 'Flood'], ['fire_zone', 'Bushfire'], ['surge_zone', 'Storm tide']];

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];
const NZ_REGIONS = ['Auckland', 'Wellington', 'Canterbury', 'Otago', 'Waikato', 'Bay of Plenty'];

export default function LocationIntelCard({ entity, form, updateForm, onRisk }) {
  const api = useApi();
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!form.suburb && !form.postcode) { setRisk(null); onRisk(null); return; }
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          address: form.street_address || '', suburb: form.suburb || '',
          state: form.state || '', postcode: form.postcode || '',
          entity_id: entity?.entity_id || '',
        });
        const { data } = await api.get(`/location/intel?${params}`);
        setRisk(data);
        onRisk(data);
      } catch (e) { setRisk(null); onRisk(null); }
      setLoading(false);
    }, 900);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.street_address, form.suburb, form.state, form.postcode]);

  const risks = risk ? [
    { icon: Waves, label: 'Flood', value: risk.flood_zone, field: 'flood_zone' },
    { icon: Flame, label: 'Bushfire', value: risk.fire_zone, field: 'fire_zone' },
    { icon: Wind, label: 'Cyclone', value: risk.cyclone_zone },
    { icon: Tornado, label: 'Storm tide', value: risk.surge_zone, field: 'surge_zone' },
    { icon: Activity, label: 'Earthquake', value: risk.earthquake_zone },
    { icon: CloudHail, label: 'Hail', value: risk.hail_risk },
    { icon: CloudLightning, label: 'Storm', value: risk.storm_risk },
    { icon: Anchor, label: 'Coast', value: `${risk.distance_to_coast_km} km`, neutral: true },
    { icon: ShieldAlert, label: 'Crime index', value: String(risk.crime_index), neutral: risk.crime_index < 55 },
  ] : [];

  return (
    <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4" data-testid="location-intel-card">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
          <MapPin className="w-4 h-4" style={{ color: C.blue }} />Location &amp; Environmental Risk
        </h3>
        {loading && <span className="flex items-center gap-1 text-[10px]" style={{ color: C.slate }} data-testid="geocoding-indicator">
          <Loader2 className="w-3 h-3 animate-spin" />Geocoding…
        </span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs" style={{ color: C.slate }}>Street Address</Label>
          <Input data-testid="input-street-address" value={form.street_address || ''}
            onChange={e => updateForm('street_address', e.target.value)} className="mt-1 rounded-sm" placeholder="e.g. 12 George St" />
        </div>
        <div>
          <Label className="text-xs" style={{ color: C.slate }}>Suburb</Label>
          <Input data-testid="input-suburb" value={form.suburb || ''}
            onChange={e => updateForm('suburb', e.target.value)} className="mt-1 rounded-sm" placeholder="e.g. Parramatta" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs" style={{ color: C.slate }}>State</Label>
            <select data-testid="select-state" value={form.state || ''} onChange={e => updateForm('state', e.target.value)}
              className="mt-1 w-full px-2 py-2 border border-input rounded-sm text-sm bg-white">
              <option value="">—</option>
              <optgroup label="AU">{AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
              <optgroup label="NZ">{NZ_REGIONS.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
            </select>
          </div>
          <div>
            <Label className="text-xs" style={{ color: C.slate }}>Postcode</Label>
            <Input data-testid="input-postcode" value={form.postcode || ''} maxLength={4}
              onChange={e => updateForm('postcode', e.target.value.replace(/\D/g, ''))} className="mt-1 rounded-sm font-mono" placeholder="2150" />
          </div>
        </div>
      </div>

      {risk && (
        <div data-testid="location-risk-panel">
          <div className="flex flex-wrap items-center gap-3 text-[10px] mb-2" style={{ color: C.slate }}>
            <span className="font-mono" data-testid="geocode-coords">📍 {risk.lat}, {risk.lng}</span>
            <span className="truncate max-w-[320px]">{risk.display_name}</span>
            <span className="px-1.5 py-0.5 rounded-sm font-medium"
              style={{ background: risk.geocode_confidence === 'high' ? 'rgba(29,158,117,0.1)' : 'rgba(232,160,32,0.12)', color: risk.geocode_confidence === 'high' ? C.green : C.amber }}>
              {risk.geocode_source === 'nominatim' ? 'OpenStreetMap geocode' : risk.geocode_source === 'entity_register' ? 'Register coordinates' : 'Approximate'}
            </span>
            <span className="ml-auto font-semibold" style={{ color: risk.hazard_score > 40 ? C.red : risk.hazard_score > 20 ? C.amber : C.green }}
              data-testid="hazard-score">Hazard score {risk.hazard_score}/100</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {risks.map(({ icon: Icon, label, value, neutral, field }) => {
              const badge = field ? (BASIS_BADGE[risk.enrichment_basis?.[field]] || HASH_BADGE) : null;
              return (
                <div key={label} className="flex items-center gap-2 p-2 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.15)' }}
                  data-testid={`risk-${label.toLowerCase().replace(/ /g, '-')}`}>
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: neutral ? C.slate : zColor(value) }} />
                  <div className="min-w-0">
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
                    <p className="text-xs font-semibold capitalize truncate" style={{ color: neutral ? C.navy : zColor(value) }}>{String(value || '—').replace(/_/g, ' ')}</p>
                  </div>
                  {badge && (
                    <span className="ml-auto text-[8px] font-semibold px-1 py-0.5 rounded uppercase shrink-0"
                      title={risk.provenance?.[field]?.source || ''}
                      style={{ background: badge.bg, color: badge.color }}
                      data-testid={`basis-${field}`}>
                      {badge.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {DETERMINED.some(([f]) => risk.hazard_evidence?.[f]?.determination) && (
            <div className="mt-2 space-y-0.5" data-testid="hazard-determinations">
              {DETERMINED.map(([f, name]) => risk.hazard_evidence?.[f]?.determination && (
                <p key={f} className="text-[10px] font-medium" style={{ color: zColor(risk[f]) }} data-testid={`determination-${f}`}>
                  {name}: {risk.hazard_evidence[f].determination}
                  {f !== 'surge_zone' && risk.hazard_evidence[f].tier && <span className="font-normal" style={{ color: C.slate }}> · decided by the {risk.hazard_evidence[f].tier} layer</span>}
                  {f === 'surge_zone' && <span className="font-normal" style={{ color: C.slate }}> · prices, never refers</span>}
                </p>
              ))}
            </div>
          )}
          {risk.hazard_attribution?.length > 0 && (
            <div className="mt-2 space-y-0.5" data-testid="hazard-attribution">
              {risk.hazard_attribution.map((line, i) => (
                <p key={i} className="text-[9px] leading-snug" style={{ color: C.slate }}>{line}</p>
              ))}
            </div>
          )}
          {risk.premium_impact?.length > 0 && (
            <p className="text-[10px] mt-2" style={{ color: C.amber }} data-testid="premium-impact-note">
              ⚠ Premium loadings will apply for: {risk.premium_impact.join(', ')}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
